import { useState } from 'react';
import type { FocusEvent, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PersonMatrix.css';

export type PersonMatrixMeta = {
    color: string;
    label: string;
};

export type PersonMatrixItem = {
    id: string | number;
    /** Full name, used as the tooltip heading and kept in the DOM for find-in-page even when the caption is clipped. */
    fullName: string;
    /** Shown below the avatar — visually clipped with CSS, never cut at the string level. */
    caption: string;
    /** Ring color around the avatar (the primary/grouping color, e.g. attendance status). */
    ringColor: string;
    /** Small satellite dot color (the secondary color, e.g. party). */
    dotColor?: string;
    photoUrl?: string | null;
    /** Extra tooltip lines, e.g. party name, status label. */
    meta?: PersonMatrixMeta[];
    linkTo?: string;
    /** Which group (e.g. party) this item belongs to — only used when `groups` is passed. */
    groupKey?: string;
};

export type PersonMatrixGroup = {
    key: string;
    label: string;
    color?: string;
    /** Shown in the group header even while collapsed, e.g. an attendance percentage for that group. */
    stat?: string;
};

export type PersonMatrixLegendItem = {
    color: string;
    label: string;
    /** Renders the swatch as a ring instead of a filled dot — use for whatever maps to the avatar ring. */
    ring?: boolean;
};

export type PersonMatrixLegendGroup = {
    label: string;
    items: PersonMatrixLegendItem[];
};

type Props = {
    items: PersonMatrixItem[];
    /** Ordered groups (e.g. parties) to render as collapsible sections once `items.length` exceeds `collapseThreshold`. */
    groups?: PersonMatrixGroup[];
    legend?: PersonMatrixLegendGroup[];
    /** Above this many items, grouped sections start collapsed instead of rendering everything at once. Default 100. */
    collapseThreshold?: number;
    className?: string;
};

const initialsOf = (name: string) => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return `${first}${last}`.toUpperCase();
};

/** Picks readable initials text color against an arbitrary ring color (some, like the yellow attendance/abstain shade, are too light for white text). */
const initialsTextColor = (color?: string) => {
    if (!color) return '#fff';
    const hexMatch = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    let r, g, b;
    if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3)
            hex = hex
                .split('')
                .map((c) => c + c)
                .join('');
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    } else {
        const rgbMatch = color.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
        if (!rgbMatch) return '#fff';
        [r, g, b] = [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
    }
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? 'var(--color-muted90)' : '#fff';
};

const PersonMatrix = ({ items, groups, legend, collapseThreshold = 100, className }: Props) => {
    const { t } = useTranslation('translation');
    const [tooltip, setTooltip] = useState<{ item: PersonMatrixItem; rect: DOMRect } | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const showTooltip = (item: PersonMatrixItem, target: HTMLElement) => {
        setTooltip({ item, rect: target.getBoundingClientRect() });
    };
    const hideTooltip = () => setTooltip(null);

    const toggleGroup = (key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const renderChip = (item: PersonMatrixItem) => {
        const content: ReactNode = (
            <>
                <span className='person-matrix-ring' style={{ borderColor: item.ringColor }}>
                    {item.photoUrl ? (
                        <span className='person-matrix-avatar photo'>
                            <img src={item.photoUrl} alt='' />
                        </span>
                    ) : (
                        <span
                            className='person-matrix-avatar initials'
                            style={{ backgroundColor: item.ringColor, color: initialsTextColor(item.ringColor) }}
                        >
                            {initialsOf(item.fullName)}
                        </span>
                    )}
                    {item.dotColor && <span className='person-matrix-dot' style={{ backgroundColor: item.dotColor }} />}
                </span>
                <span className='person-matrix-caption'>{item.caption}</span>
            </>
        );

        const onMouseEnter = (e: MouseEvent<HTMLElement>) => showTooltip(item, e.currentTarget);
        const onFocus = (e: FocusEvent<HTMLElement>) => showTooltip(item, e.currentTarget);

        return item.linkTo ? (
            <Link
                key={item.id}
                to={item.linkTo}
                className='person-matrix-chip'
                onMouseEnter={onMouseEnter}
                onMouseLeave={hideTooltip}
                onFocus={onFocus}
                onBlur={hideTooltip}
            >
                {content}
            </Link>
        ) : (
            <div
                key={item.id}
                className='person-matrix-chip'
                tabIndex={0}
                onMouseEnter={onMouseEnter}
                onMouseLeave={hideTooltip}
                onFocus={onFocus}
                onBlur={hideTooltip}
            >
                {content}
            </div>
        );
    };

    const useGroups = !!groups && groups.length > 0 && items.length > collapseThreshold;

    return (
        <div className={`person-matrix-wrap ${className || ''}`}>
            {legend && (
                <div className='person-matrix-legend'>
                    {legend.map((group, i) => (
                        <div className='person-matrix-legend-group' key={i}>
                            <span className='person-matrix-legend-label'>{group.label}</span>
                            <div className='person-matrix-legend-items'>
                                {group.items.map((legendItem, j) => (
                                    <span className='person-matrix-legend-item' key={j}>
                                        <span
                                            className={`person-matrix-legend-swatch${legendItem.ring ? ' ring' : ''}`}
                                            style={
                                                legendItem.ring
                                                    ? { borderColor: legendItem.color }
                                                    : { backgroundColor: legendItem.color }
                                            }
                                        />
                                        {legendItem.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {useGroups && (
                <div className='person-matrix-grouped-notice'>{t('PersonMatrix.groupedNotice')}</div>
            )}

            {useGroups ? (
                groups!.map((group) => {
                    const groupItems = items.filter((item) => item.groupKey === group.key);
                    if (groupItems.length === 0) return null;
                    const isOpen = expanded.has(group.key);

                    return (
                        <div className='person-matrix-group' key={group.key}>
                            <button
                                type='button'
                                className='person-matrix-group-head'
                                onClick={() => toggleGroup(group.key)}
                                aria-expanded={isOpen}
                            >
                                {group.color && <span className='person-matrix-group-swatch' style={{ backgroundColor: group.color }} />}
                                <span className='person-matrix-group-label'>{group.label}</span>
                                <span className='person-matrix-group-count'>({groupItems.length})</span>
                                {group.stat && <span className='person-matrix-group-stat'>{group.stat}</span>}
                                <span className='material-symbols-rounded person-matrix-group-chevron'>
                                    {isOpen ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>
                            {isOpen && <div className='person-matrix'>{groupItems.map(renderChip)}</div>}
                        </div>
                    );
                })
            ) : (
                <div className='person-matrix'>{items.map(renderChip)}</div>
            )}

            {tooltip && (
                <div
                    className='person-matrix-tooltip'
                    style={{
                        left: tooltip.rect.left + tooltip.rect.width / 2,
                        top: tooltip.rect.top - 8
                    }}
                >
                    <b>{tooltip.item.fullName}</b>
                    {tooltip.item.meta?.map((m, i) => (
                        <span className='person-matrix-tooltip-meta' key={i}>
                            <span className='person-matrix-tooltip-dot' style={{ backgroundColor: m.color }} />
                            {m.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PersonMatrix;
